import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import Autocomplete from 'shared/components/widgets/autocomplete';
import { ROUTES } from 'constants/routes';
import Dialog from 'shared/components/widgets/dialog';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import UploadFile from 'shared/components/widgets/upload-file';
import InputContainer from 'shared/components/widgets/input-container';
import { Controller, useForm } from 'react-hook-form';
import AutocompleteContainer from 'shared/components/widgets/autocomplete-container';
import { externalDeliveryFileUpdateService } from '@services/private/external-delivery/file-update.service';
import { is200xRange } from '@utils/utilities';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import RouteLink from 'shared/components/widgets/route-link';
import { AdminConfigEnv, Classification, DepartmentCode } from 'types';
import { externalDeliveryFileDetailService } from '@services/private/external-delivery/file-detail.service';
import { getClassification } from 'shared/helpers/external-delivery/get-classification';
import { getDepartment } from 'shared/helpers/external-delivery/get-department';
import { bytesToSize } from 'shared/helpers/bytes-to-size';
import { externalDeliveryFileUploadInitiateService } from '@services/private/external-delivery/file-upload/file-upload-initiate';
import { externalDeliveryFileUploadUrlAssignService } from '@services/private/external-delivery/file-upload/file-upload-url-assign';
import { externalDeliveryFileUploadInsertService } from '@services/private/external-delivery/file-upload/file-upload-insert';
import { externalDeliveryFileUploadCompleteService } from '@services/private/external-delivery/file-upload/file-upload-complete';
import FileUploadingDialogContent from 'shared/components/dialog-content/file-uploading';
import { externalDeliveryFileUploadStopService } from '@services/private/external-delivery/file-upload/file-upload-stop';
import { externalDeliveryFileUploadDeleteService } from '@services/private/external-delivery/file-upload/file-upload-delete';
import MultipleFieldBox from 'shared/components/widgets/multiple-field-box';

type FileData = {
  adminConfigEnv: AdminConfigEnv;
  shmDetail: {
    downloadUrl: string;
    regDt: string;
    regName: string;
    regTp: string;
    shmDeptCd: DepartmentCode;
    shmLfsKind: Classification;
    shmSeq: string;
    shmSubject: string;
    updDt: string;
    updName: string;
    updTp: string;
    uploadFileList: [
      {
        regName: string;
        regTp: string;
        shmfOriginName: string;
        shmfSeq: string;
        shmfSize: number;
      },
    ];
  } | null;
};

const ExternalDeliveryFileUpdate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [fileData, setFileData] = useState<FileData>({
    adminConfigEnv: null,
    shmDetail: null,
  });
  const [isLoading, setLoading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [shmfSeq, setShmfSeq] = useState('');
  const [uploadId, setUploadId] = useState('');

  const abortControllersRef = useRef<AbortController[]>([]);

  const { SUBJECT } = VALIDATION_MESSAGE.EXTERNAL_DELIVERY.UPDATE_FILE;

  const externalDeliveryFileUpdateSchema = yup.object({
    subject: yup
      .string()
      .required(SUBJECT.REQUIRED)
      .min(3, SUBJECT.MIN_LENGTH)
      .max(100, SUBJECT.MAX_LENGTH),
    shmLfsKind: yup.string().required('분류를 입력해 주세요'),
    shmDeptCd: yup.string().required('부서를 입력해 주세요'),
  });

  type ExternalDeliveryFileUpdateFormData = yup.InferType<
    typeof externalDeliveryFileUpdateSchema
  >;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ExternalDeliveryFileUpdateFormData>({
    defaultValues: {
      shmDeptCd: state?.fileData?.shmDetail?.shmDeptCd ?? 'DEP1000001',
      shmLfsKind: state?.fileData?.shmDetail?.shmLfsKind ?? 'proposal',
      subject: state?.fileData?.shmDetail?.shmSubject ?? ''
    },
    resolver: yupResolver(externalDeliveryFileUpdateSchema),
  });

  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadError, setUploadError] = useState('');
  const [openUploadingDialog, setOpenUploadingDialog] = useState(false);

  const handleOpenUploading = () => setOpenUploadingDialog(true);
  const handleCloseUploading = () => setOpenUploadingDialog(false);

  const classificationFilterData = [
    // { label: '전체', value: 'all' },
    { label: '제안서', value: 'proposal' },
    { label: '브로셔', value: 'brochure' },
    { label: '패치파일', value: 'patch_file' },
    { label: '설치파일', value: 'install_file' },
    { label: '가이드문서', value: 'guide' },
  ];

  const departmentFilterData = [
    // { label: '전체', value: 'all' },
    { label: 'PO본부', value: 'DEP1000001' },
    { label: 'PD본부', value: 'DEP1000002' },
    { label: 'LRD본부', value: 'DEP1000003' },
    { label: '기술본부', value: 'DEP1000004' },
    { label: '컨설팅본부', value: 'DEP1000005' },
    { label: '신사업개발본부', value: 'DEP1000006' },
    { label: '재무전략실', value: 'DEP1000007' },
    { label: '경영지원실', value: 'DEP1000008' },
    { label: '마케팅팀', value: 'DEP1000009' },
    { label: '전략실', value: 'DEP1000010' },
    { label: '법무감사실', value: 'DEP1000011' },
    { label: '전사고문', value: 'DEP1000012' },
    { label: '마크애니 체인버스', value: 'DEP1000013' },
  ];

  const getExternalDeliveryFileDetail = async (fileId: string) => {
    const { response, statusCode } =
      await externalDeliveryFileDetailService(fileId);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleFileData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'shmDetail', value: response?.result?.shmDetail },
      ]);
    }
  };

  const handleFileUpdate = async (
    shmSeq: string,
    subject: string,
    shmLfsKind: Classification,
    shmDeptCd: DepartmentCode,
  ) => {
    const { response, statusCode } = await externalDeliveryFileUpdateService({
      shmSeq,
      subject,
      shmLfsKind,
      shmDeptCd,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT);
    }
  };

  const handleFileDelete = async (shmfSeq) => {
    const { response, statusCode } =
      await externalDeliveryFileUploadDeleteService({ shmfSeq });

    if (response?.code === '1' && is200xRange(statusCode)) {
      getExternalDeliveryFileDetail(state?.fileData?.shmDetail?.shmSeq);
    }
  };

  const registerFile = async (shmSeq) => {
    const controller = new AbortController();
    abortControllersRef.current.push(controller);

    const {
      response: uploadInitiateResponse,
      statusCode: uploadInitiateStatusCode,
      error: uploadInitiateError,
    } = await externalDeliveryFileUploadInitiateService({
      shmSeq: shmSeq,
      fileName: uploadedFile?.name,
      fileSize: uploadedFile?.size,
    }, controller.signal);
    if (uploadInitiateError || !is200xRange(uploadInitiateStatusCode)) {
      handleCloseUploading();

      /*return;*/
    }

    if (
      uploadInitiateResponse?.code === '1' &&
      is200xRange(uploadInitiateStatusCode)
    ) {
      const { shmfSeq, partSize, uploadId } =
        uploadInitiateResponse?.result?.shmFileInfo;

      setUploadId(uploadId);

      const totalParts = Math.ceil(uploadedFile?.size / partSize);
      const etags: {
        partNumber: number;
        etag: string;
      }[] = [];

      setShmfSeq(shmfSeq);

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const controller2 = new AbortController();
        abortControllersRef.current.push(controller2);

        setUploadProgress(Math.trunc((partNumber / totalParts) * 100));
        const {
          response: uploadUrlAssignResponse,
          statusCode: uploadUrlAssignStatusCode,
          error: uploadUrlAssignError,
        } = await externalDeliveryFileUploadUrlAssignService({
          shmSeq: shmSeq,
          shmfSeq,
          uploadId,
          partNumber,
        }, controller2.signal);
        if (uploadUrlAssignError || !is200xRange(uploadUrlAssignStatusCode)) {
          return;
        }
        if (
          uploadUrlAssignResponse?.code === '1' &&
          is200xRange(uploadUrlAssignStatusCode)
        ) {
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, uploadedFile.size);
          const filePart = uploadedFile.slice(start, end);
          const { presignedUrl } = uploadUrlAssignResponse?.result?.shmFileInfo;
          const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: filePart,
            headers: {
              'Content-Type': uploadedFile.type,
            },
          });
          const etag = uploadResponse.headers.get('ETag');
          // @ts-ignore
          etags.push({ partNumber, etag });

          await externalDeliveryFileUploadInsertService({
            shmSeq: shmSeq,
            shmfSeq: uploadInitiateResponse?.result?.shmFileInfo?.shmfSeq,
            partNumber,
            etag,
          }, controller2.signal);
        }
      }

      const {
        response: uploadCompleteResponse,
        statusCode: uploadCompleteStatusCode,
        error: uploadCompleteError,
      } = await externalDeliveryFileUploadCompleteService({
        shmSeq: shmSeq,
        shmfSeq: uploadInitiateResponse?.result?.shmFileInfo?.shmfSeq,
        partArray: etags,
      }, controller.signal);
      if (uploadCompleteError || !is200xRange(uploadCompleteStatusCode)) {
        return;
      }

      if (
        uploadCompleteResponse?.code === '1' &&
        is200xRange(uploadCompleteStatusCode)
      ) {
        handleCloseUploading();
        navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT);
      }
    }
  };

  const stopUploading = async (
    shmSeq: string,
    shmfSeq: string,
    uploadId: string,
  ) => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current = [];

    const { response, statusCode } =
      await externalDeliveryFileUploadStopService({
        shmSeq,
        shmfSeq,
        uploadId,
      });

    if (response?.code === '1' && is200xRange(statusCode)) {
    }
  };

  const submitForm = async (data: any) => {
    const { subject, shmLfsKind, shmDeptCd } = data;

    if (state?.isUploadEdit) {
      handleOpenUploading();
      registerFile(fileData?.shmDetail?.shmSeq);
    } else {
      setLoading(true)
      await handleFileUpdate(
        state?.fileData?.shmDetail?.shmSeq,
        subject,
        shmLfsKind,
        shmDeptCd,
      );
      setLoading(false)
    }
  };

  const handleFileData = (params) => {
    setFileData((prevFileData) => {
      const newFileData = { ...prevFileData };
      params.forEach((item) => {
        newFileData[item.key] = item.value;
      });
      return newFileData;
    });
  };

  useEffect(() => {
    getExternalDeliveryFileDetail(state?.fileData?.shmDetail?.shmSeq);
  }, []);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.EXTERNAL_DELIVERY_FILES.ROOT}>Home</RouteLink>
          <RouteLink to={ROUTES.EXTERNAL_DELIVERY_FILES.ROOT}>
            외부 공유용 대용량 파일
          </RouteLink>
          <RouteLink to={ROUTES.EXTERNAL_DELIVERY_FILES.ROOT} isActive>
            대용량 등록 파일 수정
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          외부 공유용 대용량 파일 {/* LARGE FILES FOR EXTERNAL SHARING   */}
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(submitForm)}>
        {state.isUploadEdit ? (
          <Box
            display="flex"
            flexDirection="column"
            gap={6}
            marginBottom="50px"
          >
            {/* BASIC INFORMATION */}
            <FieldBox title="기본 정보">
              {/* UPLOAD TITLE */}
              <FieldBoxProperty
                name="업로드 제목"
                value={
                  <Typography variant="body1" color="var(--gray-800)">
                    {fileData?.shmDetail?.shmSubject}
                  </Typography>
                }
              />
              {/* CLASSIFICATION */}
              <FieldBoxProperty
                name="분류"
                value={
                  <Typography variant="body1" color="var(--gray-800)">
                    {getClassification(fileData?.shmDetail?.shmLfsKind)}
                  </Typography>
                }
              />
              {/* DEPARTMENT */}
              <FieldBoxProperty
                name="부서"
                value={
                  <Typography variant="body1" color="var(--gray-800)">
                    {getDepartment(state?.fileData?.shmDetail?.shmDeptCd)}
                  </Typography>
                }
              />
            </FieldBox>

            {/* UPLOAD FILE INFORMATION */}
            {/* @ts-ignore */}
            {fileData?.shmDetail?.uploadFileList.length > 0 && (
              <MultipleFieldBox title="업로드 파일 정보">
                <TableBody>
                  {fileData?.shmDetail?.uploadFileList?.map(
                    (uploadedFile, index) => {
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography
                                variant="body1"
                                color="var(--gray-800)"
                              >
                                {uploadedFile?.shmfOriginName}
                              </Typography>
                              <Button
                                variant="outlined"
                                color="secondary"
                                sx={{
                                  padding: '6px 14px !important',
                                }}
                                onClick={() =>
                                  handleFileDelete(uploadedFile?.shmfSeq)
                                }
                              >
                                삭제
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {bytesToSize(uploadedFile?.shmfSize) ?? '--'}
                          </TableCell>
                          <TableCell>
                            {uploadedFile?.regTp === 'admin'
                              ? '관리자'
                              : uploadedFile?.regTp === 'staff'
                                ? '직원'
                                : '--'}
                          </TableCell>
                          <TableCell>{uploadedFile?.regName ?? '--'}</TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </MultipleFieldBox>
            )}
            {/* UPLOAD FILE */}
            <FieldBox title="업로드 파일 추가하기">
              <FieldBoxProperty
                name="* 변경할 업로드 파일"
                value={
                  <Box display="flex" flexDirection="column" gap={3}>
                    <UploadFile
                      uploadedFile={uploadedFile}
                      setUploadedFile={setUploadedFile}
                    />
                    {!uploadedFile && uploadError && (
                      <Typography color={'var(--danger-500)'}>
                        {uploadError}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </FieldBox>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            gap={6}
            marginBottom="50px"
          >
            {/* BASIC INFORMATION */}
            <FieldBox title="기본 정보">
              {/* UPLOAD TITLE */}
              <FieldBoxProperty
                name="* 업로드 제목"
                value={
                  <Controller
                    name="subject"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          <Input
                            {...field}
                            placeholder="검색어를 입력해 주세요."
                            defaultValue={field.value}
                            showErrorMsg={!!errors.subject}
                            errorMessage={
                              errors.subject && errors.subject.message
                            }
                          />
                        </InputContainer>
                      );
                    }}
                  />
                }
              />
              {/* CLASSIFICATION */}
              <FieldBoxProperty
                name="* 분류"
                value={
                  <AutocompleteContainer type="form">
                    <Controller
                      name="shmLfsKind"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            {...field}
                            options={classificationFilterData}
                            defaultValue={
                              classificationFilterData.find(
                                (el) => el.value === field?.value,
                              ) ?? {
                                label: '제안서',
                                value: 'proposal',
                              }
                            }
                            value={field?.value ?? 'proposal'}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            onChange={(_, newValue, reason) => {
                              field.onChange(newValue?.value);
                              if (reason === 'clear') {
                                field.onChange(state?.fileData?.shmDetail?.shmLfsKind);
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </AutocompleteContainer>
                }
              />
              {/* DEPARTMENT */}
              <FieldBoxProperty
                name="* 부서"
                value={
                  <AutocompleteContainer type="form">
                    <Controller
                      name="shmDeptCd"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            {...field}
                            options={departmentFilterData}
                            defaultValue={
                              departmentFilterData.find(
                                (el) => el.value === field?.value,
                              ) ?? {
                                label: 'PO본부',
                                value: 'DEP1000001',
                              }
                            }
                            value={field?.value ?? 'DEP1000001'}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            onChange={(_, newValue, reason) => {
                              field.onChange(newValue?.value);
                              if (reason === 'clear') {
                                field.onChange(state?.fileData?.shmDetail?.shmDeptCd);
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </AutocompleteContainer>
                }
              />
            </FieldBox>

            {/* UPLOAD FILE INFORMATION */}
            <MultipleFieldBox title="업로드 파일 정보">
              <TableBody>
                {fileData?.shmDetail?.uploadFileList?.map(
                  (uploadedFile, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          {uploadedFile?.shmfOriginName ?? '--'}
                        </TableCell>
                        <TableCell>
                          {bytesToSize(uploadedFile?.shmfSize) ?? '--'}
                        </TableCell>
                        <TableCell>
                          {uploadedFile?.regTp === 'admin'
                            ? '관리자'
                            : uploadedFile?.regTp === 'staff'
                              ? '직원'
                              : '--'}
                        </TableCell>
                        <TableCell>{uploadedFile?.regName ?? '--'}</TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </MultipleFieldBox>
          </Box>
        )}

        <Box>
          <Box display="flex" gap="14px">
            {/* CANCEL */}
            <Button
              disabled={isLoading}
              variant="outlined"
              color="secondary"
              onClick={() => navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT)}
            >
              취소
            </Button>
            {/* SAVE */}
            <Button
              disabled={isLoading}
              variant="contained"
              color="primary"
              sx={{ width: '86px' }}
              type={state.isUploadEdit && uploadError ? 'button' : 'submit'}
              onClick={() => {
                if (!uploadedFile) {
                  setUploadError('파일을 업로드 해주세요.');
                } else {
                  setUploadError('');
                }
              }}
            >
              {isLoading ?
                <CircularProgress size={18} color='secondary' />
                :
                '완료'
              }
            </Button>
          </Box>
        </Box>
      </Box>
      <Dialog
        isBackDropClickClose={false}
        open={openUploadingDialog}
        closeAction={handleCloseUploading}
        width={348}
        height={304}
        isCloseIcon={false}
      >
        <FileUploadingDialogContent
          closeAction={handleCloseUploading}
          progress={uploadProgress}
          stopAction={() =>
            // @ts-ignore
            stopUploading(fileData?.shmDetail?.shmSeq, shmfSeq, uploadId)
          }
        />
      </Dialog>
    </Box>
  );
};

export default ExternalDeliveryFileUpdate;
